import { EnumRichiestaDTOStato } from '@framework/api-client';
import { useCurrentUser } from '@framework/auth';
import {
  InquiryTypeStateTag,
  SubmissionResponsiveCard,
  TitleSubmissionRow,
} from '@framework/common-web';
import { EngineThemeProvider } from '@framework/engine';
import { FormCoordinator } from '@framework/form';
import {
  DelegationContextProvider,
  DelegationMachine,
  FlexSubmissionRow,
  isDelegate,
  ModuleFormSectionsMonitorContextProvider,
  SubmissionLogic,
  SubmissionMenu,
  SubmissionPaymentsCard,
  SubmissionStateTag,
  useSubmission,
  useSubmissionMenuItems,
  useSubmissionModules,
  useSubmissionNavigation,
} from '@framework/frontoffice-submission';
import { useTranslation } from '@framework/i18n';
import {
  BrandedPath,
  generatePath,
  Route,
  useBreadcrumbItem,
  useHistory,
  useIdFromUrl,
} from '@framework/ui';
import { Space } from 'antd';
import { useMemo } from 'react';
import { RouteComponentProps, Switch } from 'react-router';
import { CompilationGuideCard } from '../../components/submissions/cards/compilation-guide/CompilationGuideCard';
import { CompilationModuleCard } from '../../components/submissions/cards/module/CompilationModuleCard';
import { CompilationSendCard } from '../../components/submissions/cards/send/CompilationSendCard';
import { getSubmissionMenuLink } from '../../components/submissions/hooks/getSubmissionMenuLink';
import { PageLayout } from '../../ui/layout/PageLayout';
import { NetworkAlertPage } from '../errors/network-alert/NetworkAlertPage';
import { LoadingPage } from '../loading/LoadingPage';
import { ContentCardWrapper } from './components/ContentCardWrapper';
import { InPaymentBanner } from './components/InPaymentBanner';
import { SubmissionPrincipalModuleRedirector } from './components/principal-module-redirector/SubmissionPrincipalModuleRedirector';
import { SubmissionDelegationBanner } from './components/SubmissionDelegationBanner';
import { SubmissionDelegationButton } from './components/SubmissionDelegationButton';
import { SubmissionCloneButton } from './components/SubmissionDuplicateButton';
import { SubmissionLinkedBox } from './components/SubmissionLinkedBox';
import { useDelegationData } from './delegation/hooks/useDelegationData';
import { IntegrationBanner } from './sent/IntegrationBanner';

interface Params {
  id: string;
  moduleId: string;
}

export interface ISubmissionPageProps extends RouteComponentProps<Params> {}

export function SubmissionPage(props: ISubmissionPageProps) {
  const [submissionId] = useIdFromUrl(props, 'id');
  const [submissionModuleId] = useIdFromUrl(props, 'moduleId');
  const history = useHistory();
  const { t } = useTranslation();
  const currentUser = useCurrentUser();

  const { submission, inquiry, title, submissionRequest, inquiryRequest } =
    useSubmission(submissionId);

  const submissionModules = useSubmissionModules({ submissionId });

  const hasIntegrations = SubmissionLogic.hasIntegrations(submission);

  const {
    delegationData,
    isLoading,
    error: delegationError,
  } = useDelegationData({
    modules: submissionModules.modules,
    applicant: submission?.datiTitolari,
    submissionId,
  });
  const canViewDelegates = DelegationMachine.can(
    delegationData,
    '.view',
    currentUser,
  );

  const canSubmit = DelegationMachine.can(
    delegationData,
    '.canSubmit',
    currentUser,
  );

  const sentable = useMemo(() => {
    return inquiry && !inquiry.flNonInviabile;
  }, [inquiry]);

  const { menuItems: submissionMenuItems } = useSubmissionMenuItems({
    modules: submissionModules.modules,
    submissionId,
    submission,
    sentable,
    getMenuLink: getSubmissionMenuLink,
    canSubmit: canSubmit.allowed,
  });

  const handleNavigation = useSubmissionNavigation({
    submissionId,
    steps: submissionMenuItems,
  });

  // Breadcrumbs
  useBreadcrumbItem({
    path: '/richieste',
    title: t('submission-list.richieste', 'Richieste'),
  });
  useBreadcrumbItem({
    path: props.location.pathname as BrandedPath,
    title: title,
  });

  // Redirect alla pagina di processamento (in invio)
  if (SubmissionLogic.isSubmissionPending(submission) && submissionId) {
    history.replace(
      generatePath('/richieste/:id/risultato', { id: submissionId }),
    );
  }

  if (SubmissionLogic.isSubmissionSent(submission) && submissionId) {
    history.replace(
      generatePath('/richieste/:id/inviata', { id: submissionId }),
    );
  }

  // Controllo se l'utente è delegato
  const isSubmissionDelegate = useMemo(() => {
    if (isLoading) return false;
    return isDelegate(delegationData, currentUser);
  }, [currentUser, delegationData, isLoading]);

  const showDelegateBanner = useMemo(() => {
    return currentUser?.utenteLoggato?.idUtente !== submission?.idRichiedente;
  }, [currentUser, submission]);

  const isInPayment = SubmissionLogic.isSubmissionInPayment(submission);

  // Mostro il pulsante di delega se la richiesta non è stata inviata e non è già delegata
  // oltre a verificare se la richiesta permette la delega
  const showDelegationButton =
    !SubmissionLogic.isSubmissionSent(submission) &&
    inquiryRequest.data?.permetteDelega &&
    canViewDelegates.allowed &&
    !SubmissionLogic.isInquiryPreview(inquiry);

  const showCloneButton =
    !inquiryRequest.data?.flRichiestaUnivoca &&
    DelegationMachine.can(delegationData, '.canDuplicate', currentUser)
      .allowed &&
    !SubmissionLogic.isInquiryPreview(inquiry);

  if (
    submissionModules.isLoading ||
    submissionRequest.isLoading ||
    inquiryRequest.isLoading ||
    isLoading
  ) {
    return <LoadingPage />;
  }

  if (
    submissionModules.isError ||
    submissionRequest.isError ||
    inquiryRequest.isError ||
    // In caso di delegati, gli endpoint di delega non sono autorizzati
    // e tornano 403, ma non è un errore da mostrare all'utente
    // In ogni caso le visibilità sono comunque gestite per gli altri endpoint
    (delegationError && delegationError?.response?.status !== 403)
  ) {
    return (
      <NetworkAlertPage
        error={
          submissionRequest.error ??
          inquiryRequest.error ??
          submissionModules.error ??
          delegationError
        }
        message={t(
          'submission-form.errore-in-caricamento-domanda',
          'Si è verificato un errore durante il caricamento della domanda.',
        )}
      />
    );
  }

  return (
    <PageLayout>
      <DelegationContextProvider delegationData={delegationData}>
        <SubmissionLinkedBox submission={submission} inquiry={inquiry} />
        <ContentCardWrapper submission={submission}>
          <SubmissionResponsiveCard
            title={
              <TitleSubmissionRow
                // left={<SubmissionStateTag submission={submission} />}
                stateTag={
                  inquiry && SubmissionLogic.isInquiryPreview(inquiry) ? (
                    <InquiryTypeStateTag inquiry={inquiry} />
                  ) : (
                    <SubmissionStateTag
                      submission={submission}
                      inquiry={inquiry}
                    />
                  )
                }
                right={
                  <Space>
                    {showCloneButton && (
                      <SubmissionCloneButton
                        isJustCreated={
                          submission?.stato === EnumRichiestaDTOStato.CREATA
                        }
                        submissionId={submissionId}
                      />
                    )}
                    {showDelegationButton && (
                      <SubmissionDelegationButton submission={submission} />
                    )}
                  </Space>
                }
              >
                {title}
              </TitleSubmissionRow>
            }
            cover={
              <>
                {isInPayment && (
                  <InPaymentBanner
                    submissionId={submissionId!}
                    burdenStatus={submission?.statoOneri}
                  />
                )}
                {isSubmissionDelegate && showDelegateBanner ? (
                  <SubmissionDelegationBanner />
                ) : null}
                {hasIntegrations && (
                  <IntegrationBanner
                    integrationID={null}
                    message={t(
                      'submission-form.integrazione-di-un-altra-richiesta',
                      "Questa è un'integrazione di un'altra richiesta.",
                    )}
                    originalRequestId={submission?.richiestaOriginale?.id}
                  />
                )}
              </>
            }
          >
            <FormCoordinator>
              <ModuleFormSectionsMonitorContextProvider>
                <FlexSubmissionRow
                  menuSide="right"
                  menu={
                    <SubmissionMenu
                      items={submissionMenuItems}
                      currentSubmissionModuleId={
                        submissionModuleId ?? '<unknown>'
                      }
                    />
                  }
                  mobileRules={{ showBefore: true }}
                >
                  <EngineThemeProvider>
                    <Switch>
                      <Route
                        exact
                        path="/richieste/:id"
                        render={(props: any) => (
                          <CompilationGuideCard
                            {...props}
                            handleNavigation={handleNavigation}
                          />
                        )}
                      />
                      <Route
                        exact
                        path="/richieste/:id/moduli/:moduleId"
                        render={(props: any) => (
                          <CompilationModuleCard
                            {...props}
                            readonly={
                              SubmissionLogic.isInquiryPreview(inquiry)
                                ? true
                                : // Non possiamo ritornare false, altrimenti sovrascriviamo
                                  // la gestione interna di readonly
                                  undefined
                            }
                            handleNavigation={handleNavigation}
                            inquiry={inquiry}
                          />
                        )}
                      />
                      <Route
                        path="/richieste/:id/modulo-principale"
                        render={() => (
                          <SubmissionPrincipalModuleRedirector
                            submissionModules={submissionModules}
                            submissionId={submissionId}
                          />
                        )}
                      />
                      <Route
                        exact
                        path="/richieste/:id/pagamenti"
                        render={() => (
                          <SubmissionPaymentsCard
                            submissionId={submissionId!}
                            handleNavigation={handleNavigation}
                            submission={submission!}
                          />
                        )}
                      />
                      <Route
                        exact
                        path="/richieste/:id/invia"
                        render={(props: any) => (
                          <CompilationSendCard
                            {...props}
                            handleNavigation={handleNavigation}
                            canSubmit={canSubmit}
                          />
                        )}
                      />
                    </Switch>
                  </EngineThemeProvider>
                </FlexSubmissionRow>
              </ModuleFormSectionsMonitorContextProvider>
            </FormCoordinator>
          </SubmissionResponsiveCard>
        </ContentCardWrapper>
      </DelegationContextProvider>
    </PageLayout>
  );
}
